import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Sparkles, CheckCircle, XCircle, Send, ChevronDown, ChevronUp,
  Trash2, RefreshCw, User, Brain, X, Pencil, Plus, Flag, Wand2, Lock,
} from 'lucide-react';
import { ExtractedPersona, UserStory } from '../../services/workshop';
import {
  updateStoryApproval,
  updateStoryContent,
  DeepDiveMessage,
  StoryAction,
} from '../../services/n8n';
import { api } from '../../services/api';
import { toast } from 'sonner';

interface PersonaReviewModalProps {
  sessionId: string;
  persona: ExtractedPersona;
  stories: UserStory[];
  allStories: UserStory[];
  onClose: () => void;
  onReloadData: () => void;
}

type ApprovalStatus = 'pending' | 'approved' | 'rejected';

interface EditDraft {
  story: string;
  acceptanceCriteria: string[];
  priority: 'high' | 'medium' | 'low';
}

const POLL_INTERVAL = 4000;

const makeMdComponents = (textColor: string): Components => ({
  p: ({ children }) => <p className={`text-sm leading-relaxed mb-2 last:mb-0 ${textColor}`}>{children}</p>,
  h1: ({ children }) => <h1 className="text-base font-bold text-slate-900 mt-3 mb-1.5">{children}</h1>,
  h2: ({ children }) => <h2 className="text-sm font-bold text-slate-800 mt-2.5 mb-1 pb-0.5 border-b border-slate-200">{children}</h2>,
  h3: ({ children }) => <h3 className="text-sm font-semibold text-slate-700 mt-2 mb-1">{children}</h3>,
  ul: ({ children }) => <ul className="list-disc list-outside ml-4 mb-2 space-y-0.5">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-outside ml-4 mb-2 space-y-0.5">{children}</ol>,
  li: ({ children }) => <li className={`text-sm leading-relaxed ${textColor}`}>{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-slate-900">{children}</strong>,
  em: ({ children }) => <em className="italic text-slate-600">{children}</em>,
  code: ({ children, className }) => {
    const isBlock = className?.includes('language-');
    return isBlock ? (
      <pre className="bg-slate-800 text-slate-100 rounded-lg p-3 my-2 overflow-x-auto text-xs"><code>{children}</code></pre>
    ) : (
      <code className="bg-slate-200/60 text-slate-800 px-1 py-0.5 rounded text-xs font-mono">{children}</code>
    );
  },
  table: ({ children }) => (
    <div className="overflow-x-auto my-2 rounded-lg border border-slate-200">
      <table className="w-full text-sm border-collapse">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-slate-100 border-b border-slate-200">{children}</thead>,
  th: ({ children }) => <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600">{children}</th>,
  td: ({ children }) => <td className="px-3 py-2 text-sm text-slate-700 border-b border-slate-100 last:border-b-0">{children}</td>,
  hr: () => <hr className="my-3 border-slate-200" />,
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-primary/40 pl-3 my-2 text-sm italic text-slate-600 bg-slate-50/60 py-1.5 rounded-r">{children}</blockquote>
  ),
});

const ACTION_CONFIG: Record<StoryAction['type'], { label: (a: StoryAction) => string; color: string; icon: React.ReactNode }> = {
  approve:      { label: a => `Approve #${a.storyNumber}`,      color: 'border-green-200 text-green-700 hover:bg-green-50',    icon: <CheckCircle size={11} /> },
  reject:       { label: a => `Reject #${a.storyNumber}`,       color: 'border-red-200 text-red-600 hover:bg-red-50',          icon: <XCircle size={11} /> },
  set_priority: { label: a => `#${a.storyNumber} → ${a.priority}`, color: 'border-amber-200 text-amber-700 hover:bg-amber-50', icon: <Flag size={11} /> },
  add_ac:       { label: a => `Add AC to #${a.storyNumber}`,    color: 'border-blue-200 text-blue-700 hover:bg-blue-50',       icon: <Plus size={11} /> },
  reword:       { label: a => `Reword #${a.storyNumber}`,       color: 'border-purple-200 text-purple-700 hover:bg-purple-50', icon: <Pencil size={11} /> },
};

const ActionChip: React.FC<{ action: StoryAction; executing: boolean; onClick: () => void }> = ({ action, executing, onClick }) => {
  const cfg = ACTION_CONFIG[action.type];
  return (
    <button
      onClick={onClick}
      disabled={executing}
      className={`flex items-center gap-1 px-2 py-1 rounded-lg border text-[11px] font-semibold transition-colors bg-white disabled:opacity-50 ${cfg.color}`}
    >
      {executing ? <span className="w-3 h-3 rounded-full border-2 border-current border-t-transparent animate-spin" /> : cfg.icon}
      {cfg.label(action)}
    </button>
  );
};

// ── Story row sub-components ──────────────────────────────────────────────────

function storyNumClass(status: string): string {
  if (status === 'approved') return 'text-green-600';
  if (status === 'rejected') return 'text-red-400';
  return 'text-primary';
}

function priorityPillClass(priority: string): string {
  if (priority === 'high') return 'bg-red-100 text-red-600';
  if (priority === 'medium') return 'bg-amber-100 text-amber-600';
  return 'bg-slate-100 text-slate-500';
}

function getRowBackground(isEditing: boolean, status: ApprovalStatus): string {
  if (isEditing) return 'bg-primary/[0.03] border-l-2 border-primary';
  if (status === 'approved') return 'bg-green-50/50';
  if (status === 'rejected') return 'bg-red-50/30 opacity-60';
  return '';
}

const StoryEditForm: React.FC<{
  idx: number;
  draft: EditDraft;
  saving: boolean;
  onChange: (d: EditDraft) => void;
  onSave: () => void;
  onCancel: () => void;
  onUpdateAc: (i: number, v: string) => void;
  onRemoveAc: (i: number) => void;
  onAddAc: () => void;
}> = ({ idx, draft, saving, onChange, onSave, onCancel, onUpdateAc, onRemoveAc, onAddAc }) => (
  <div className="flex gap-3">
    <span className="text-xs font-bold text-primary font-mono mt-1 flex-shrink-0 w-6">#{idx + 1}</span>
    <div className="flex-1 min-w-0 flex flex-col gap-2">
      <textarea
        value={draft.story}
        onChange={e => onChange({ ...draft, story: e.target.value })}
        rows={3}
        className="w-full border border-primary/40 rounded-xl px-3 py-2 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none"
      />
      <div className="flex rounded-lg border border-slate-200 overflow-hidden">
        {(['high', 'medium', 'low'] as const).map(p => (
          <button key={p} type="button" onClick={() => onChange({ ...draft, priority: p })}
            className={`flex-1 py-1 text-xs font-semibold transition-all capitalize ${draft.priority === p ? 'bg-primary text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}>
            {p}
          </button>
        ))}
      </div>
      <div>
        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Acceptance Criteria</p>
        <div className="space-y-1">
          {draft.acceptanceCriteria.map((ac, i) => (
            <div key={`ac-edit-${i}`} className="flex items-center gap-1"> {/* NOSONAR */}
              <input value={ac} onChange={e => onUpdateAc(i, e.target.value)}
                className="flex-1 border border-slate-200 rounded-lg px-2 py-1 text-xs text-navy focus:ring-1 focus:ring-primary focus:border-transparent outline-none" />
              <button onClick={() => onRemoveAc(i)}
                className="flex-shrink-0 w-5 h-5 flex items-center justify-center text-slate-400 hover:text-red-500 transition-colors">
                <X size={12} />
              </button>
            </div>
          ))}
          <button onClick={onAddAc}
            className="flex items-center gap-1 text-xs text-primary hover:text-primary-dark font-semibold transition-colors">
            <Plus size={11} /> Add criterion
          </button>
        </div>
      </div>
      <div className="flex items-center gap-2 pt-1">
        <button onClick={onSave} disabled={saving}
          className="flex items-center gap-1.5 rounded-lg h-7 px-3 bg-primary hover:bg-primary-dark transition-colors text-white text-xs font-bold disabled:opacity-50">
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button onClick={onCancel}
          className="flex items-center gap-1.5 rounded-lg h-7 px-3 border border-slate-200 text-slate-500 text-xs font-semibold hover:bg-slate-50 transition-colors">
          Cancel
        </button>
      </div>
    </div>
  </div>
);

const StoryViewRow: React.FC<{
  story: UserStory;
  idx: number;
  status: string;
  expanded: boolean;
  onToggleExpand: () => void;
  onStartEdit: () => void;
  onApprove: () => void;
  onReject: () => void;
}> = ({ story, idx, status, expanded, onToggleExpand, onStartEdit, onApprove, onReject }) => {
  const isLocked = !!story.locked;
  return (
  <div className="flex items-start gap-3">
    <span className={`text-xs font-bold font-mono mt-0.5 flex-shrink-0 w-6 ${storyNumClass(status)}`}>#{idx + 1}</span>
    <div className="flex-1 min-w-0">
      <p className={`text-sm text-navy leading-snug ${status === 'rejected' ? 'line-through' : ''}`}>{story.story}</p>
      <div className="flex items-center gap-2 mt-1 flex-wrap">
        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${priorityPillClass(story.priority)}`}>{story.priority}</span>
        {isLocked && (
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 flex items-center gap-0.5">
            <Lock size={9} />Locked
          </span>
        )}
        {story.acceptanceCriteria?.length > 0 && (
          <button onClick={onToggleExpand} className="text-[10px] text-slate-400 hover:text-slate-600 flex items-center gap-0.5">
            {expanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
            {story.acceptanceCriteria.length} criteria
          </button>
        )}
      </div>
      {expanded && story.acceptanceCriteria?.length > 0 && (
        <ul className="mt-2 space-y-0.5">
          {story.acceptanceCriteria.map(ac => (
            <li key={`${story.id}-ac-${ac}`} className="text-xs text-slate-500 flex items-start gap-1">
              <span className="text-primary mt-0.5">•</span> {ac}
            </li>
          ))}
        </ul>
      )}
    </div>
    <div className="flex items-center gap-1 flex-shrink-0">
      <button onClick={onStartEdit} title={isLocked ? 'Story is locked — unlock to edit' : 'Edit story'} disabled={isLocked}
        className="flex items-center justify-center w-7 h-7 rounded-lg border border-slate-200 text-slate-400 hover:border-primary/40 hover:text-primary transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-slate-200 disabled:hover:text-slate-400">
        <Pencil size={12} />
      </button>
      <button onClick={onApprove} title={isLocked ? 'Story is locked' : 'Approve'} disabled={isLocked}
        className={`flex items-center justify-center w-7 h-7 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${status === 'approved' ? 'bg-green-500 text-white' : 'border border-slate-200 text-slate-400 hover:border-green-400 hover:text-green-500'}`}>
        <CheckCircle size={14} />
      </button>
      <button onClick={onReject} title={isLocked ? 'Story is locked' : 'Reject'} disabled={isLocked}
        className={`flex items-center justify-center w-7 h-7 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${status === 'rejected' ? 'bg-red-500 text-white' : 'border border-slate-200 text-slate-400 hover:border-red-400 hover:text-red-500'}`}>
        <XCircle size={14} />
      </button>
    </div>
  </div>
  );
};

const ChatMessage: React.FC<{
  message: DeepDiveMessage;
  personaName: string;
  onAction?: (action: StoryAction) => Promise<void>;
  executingAction?: string | null;
}> = ({ message, personaName, onAction, executingAction }) => {
  const isPersona = message.agentType === 'persona';
  const isFacilitator = message.personaName === 'Facilitator';
  const hasActions = !isPersona && !isFacilitator && message.actions && message.actions.length > 0;

  if (isFacilitator) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[80%] rounded-2xl rounded-br-sm bg-navy text-white px-4 py-2.5">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={makeMdComponents('text-white')}>
            {message.message}
          </ReactMarkdown>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-3">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
        isPersona ? 'bg-primary/10 border border-primary/20' : 'bg-blue-50 border border-blue-200'
      }`}>
        {isPersona ? <User size={14} className="text-primary" /> : <Brain size={14} className="text-blue-500" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className={`text-[11px] font-bold tracking-wide ${isPersona ? 'text-primary' : 'text-blue-600'}`}>
            {isPersona ? personaName : 'UX Specialist'}
          </span>
          <span className="text-[10px] text-slate-400">
            {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
        <div className={`rounded-2xl rounded-tl-sm px-4 py-2.5 ${
          isPersona ? 'bg-primary/5 border border-primary/10' : 'bg-blue-50 border border-blue-100'
        }`}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={makeMdComponents('text-slate-700')}>
            {message.message}
          </ReactMarkdown>
        </div>
        {hasActions && onAction && (
          <div className="mt-2 flex items-center gap-1.5 flex-wrap">
            <div className="flex items-center gap-1 text-[10px] text-slate-400 font-semibold">
              <Wand2 size={10} /> Actions
            </div>
            {message.actions!.map((action) => (
              <ActionChip
                key={`${action.type}-${action.storyId}`}
                action={action}
                executing={executingAction === `${action.type}-${action.storyId}`}
                onClick={() => onAction(action)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export const PersonaReviewModal: React.FC<PersonaReviewModalProps> = ({
  sessionId,
  persona,
  stories,
  allStories,
  onClose,
  onReloadData,
}) => {
  const [messages, setMessages] = useState<DeepDiveMessage[]>([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [userMessage, setUserMessage] = useState('');
  const [expandedStories, setExpandedStories] = useState<Set<string>>(new Set());
  const [localApprovals, setLocalApprovals] = useState<Record<string, ApprovalStatus>>({});
  const [editingStoryId, setEditingStoryId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<EditDraft | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [executingAction, setExecutingAction] = useState<string | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const knownMessageIds = useRef<Set<string>>(new Set());

  const getApprovalStatus = (story: UserStory): ApprovalStatus =>
    localApprovals[story.id] ?? story.approvalStatus ?? 'pending';

  // Scroll to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load existing messages on open
  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get(`/api/sessions/${sessionId}/personas/${persona.id}/review-chat/messages`);
        const msgs: DeepDiveMessage[] = res.data.messages || [];
        msgs.forEach(m => knownMessageIds.current.add(m.id));
        setMessages(msgs);
      } catch {}
    };
    load();
  }, [sessionId, persona.id]);

  // Poll for new messages
  const pollMessages = useCallback(async () => {
    try {
      const res = await api.get(`/api/sessions/${sessionId}/personas/${persona.id}/review-chat/messages`);
      const all: DeepDiveMessage[] = res.data.messages || [];
      const fresh = all.filter(m => !knownMessageIds.current.has(m.id));
      if (fresh.length > 0) {
        fresh.forEach(m => knownMessageIds.current.add(m.id));
        setMessages(prev => [...prev, ...fresh]);
        if (fresh.some(m => m.agentType === 'specialist' || m.agentType === 'persona')) {
          setAnalyzing(false);
        }
      }
    } catch {}
  }, [sessionId, persona.id]);

  useEffect(() => {
    pollRef.current = setInterval(pollMessages, POLL_INTERVAL);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [pollMessages]);

  const buildPayload = useCallback(() =>
    stories.map((s, idx) => ({
      id: s.id,
      number: idx + 1,
      story: s.story,
      acceptanceCriteria: s.acceptanceCriteria,
      priority: s.priority,
    })),
  [stories]);

  const handleAnalyze = useCallback(async () => {
    setAnalyzing(true);
    try {
      await api.post(`/api/sessions/${sessionId}/personas/${persona.id}/review-chat/analyze`, {
        personaName: persona.name,
        personaRole: persona.role,
        personaDescription: persona.description,
        stories: buildPayload(),
      });
    } catch {
      toast.error('Failed to start analysis');
      setAnalyzing(false);
    }
  }, [sessionId, persona, buildPayload]);

  const handleApproval = useCallback(async (storyId: string, status: ApprovalStatus) => {
    setLocalApprovals(prev => ({ ...prev, [storyId]: status }));
    try {
      await updateStoryApproval(sessionId, storyId, status);
      onReloadData();
    } catch {
      toast.error('Failed to save approval');
    }
  }, [sessionId, onReloadData]);

  const handleClearChat = useCallback(async () => {
    await api.delete(`/api/sessions/${sessionId}/personas/${persona.id}/review-chat/messages`);
    setMessages([]);
    knownMessageIds.current.clear();
  }, [sessionId, persona.id]);

  const handleUserMessage = useCallback(async () => {
    if (!userMessage.trim()) return;
    const text = userMessage.trim();
    setUserMessage('');
    setMessages(prev => [...prev, {
      id: `facilitator-${Date.now()}`,
      agentType: 'specialist',
      personaId: persona.id,
      personaName: 'Facilitator',
      message: text,
      timestamp: new Date().toISOString(),
    }]);
    setAnalyzing(true);
    try {
      await api.post(`/api/sessions/${sessionId}/personas/${persona.id}/review-chat/analyze`, {
        personaName: persona.name,
        personaRole: persona.role,
        personaDescription: persona.description,
        stories: buildPayload(),
      });
    } catch {
      toast.error('Failed to send message');
      setAnalyzing(false);
    }
  }, [userMessage, sessionId, persona, buildPayload]);

  const handleAction = useCallback(async (action: StoryAction) => {
    const targetStory = allStories.find(s => s.id === action.storyId);
    if (targetStory?.locked) {
      toast.error('Story is locked — unlock it to apply this action');
      return;
    }
    const key = `${action.type}-${action.storyId}`;
    setExecutingAction(key);
    try {
      switch (action.type) {
        case 'approve':
          await handleApproval(action.storyId, 'approved');
          break;
        case 'reject':
          await handleApproval(action.storyId, 'rejected');
          break;
        case 'set_priority':
          if (action.priority) {
            await updateStoryContent(sessionId, action.storyId, { priority: action.priority });
            onReloadData();
          }
          break;
        case 'add_ac': {
          const story = allStories.find(s => s.id === action.storyId);
          if (story && action.criteria?.length) {
            const merged = [...(story.acceptanceCriteria || []), ...action.criteria];
            await updateStoryContent(sessionId, action.storyId, { acceptanceCriteria: merged });
            onReloadData();
          }
          break;
        }
        case 'reword': {
          const story = allStories.find(s => s.id === action.storyId);
          if (story) {
            setEditingStoryId(action.storyId);
            setEditDraft({
              story: action.suggestion || story.story,
              acceptanceCriteria: [...(story.acceptanceCriteria || [])],
              priority: story.priority,
            });
          }
          return;
        }
      }
      toast.success('Applied');
    } catch {
      toast.error('Action failed');
    } finally {
      setExecutingAction(null);
    }
  }, [allStories, sessionId, handleApproval, onReloadData]);

  const handleSaveEdit = useCallback(async (storyId: string) => {
    if (!editDraft) return;
    setSavingEdit(true);
    try {
      await updateStoryContent(sessionId, storyId, editDraft);
      onReloadData();
      setEditingStoryId(null);
      setEditDraft(null);
      toast.success('Story updated');
    } catch {
      toast.error('Failed to save story');
    } finally {
      setSavingEdit(false);
    }
  }, [editDraft, sessionId, onReloadData]);

  const updateAcLine = useCallback((idx: number, value: string) => {
    setEditDraft(prev => prev ? { ...prev, acceptanceCriteria: prev.acceptanceCriteria.map((ac, i) => i === idx ? value : ac) } : null);
  }, []);
  const removeAcLine = useCallback((idx: number) => {
    setEditDraft(prev => prev ? { ...prev, acceptanceCriteria: prev.acceptanceCriteria.filter((_, i) => i !== idx) } : null);
  }, []);
  const addAcLine = useCallback(() => {
    setEditDraft(prev => prev ? { ...prev, acceptanceCriteria: [...prev.acceptanceCriteria, ''] } : null);
  }, []);

  const handleToggleExpand = useCallback((storyId: string) => {
    setExpandedStories(prev => {
      const next = new Set(prev);
      if (next.has(storyId)) next.delete(storyId); else next.add(storyId);
      return next;
    });
  }, []);

  const handleStartEdit = useCallback((story: UserStory) => {
    if (story.locked) return;
    setEditingStoryId(story.id);
    setEditDraft({ story: story.story, acceptanceCriteria: [...(story.acceptanceCriteria || [])], priority: story.priority });
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingStoryId(null);
    setEditDraft(null);
  }, []);

  const handleApprovalToggle = useCallback((storyId: string, currentStatus: ApprovalStatus, target: 'approved' | 'rejected') => {
    if (allStories.find(s => s.id === storyId)?.locked) return;
    handleApproval(storyId, currentStatus === target ? 'pending' : target);
  }, [allStories, handleApproval]);

  // Ask session avatars (Devil's Advocate etc.) — not this customer persona's own
  // chat above — to review the whole story set for this persona.
  const handleAvatarReviewAll = useCallback(async () => {
    try {
      const res = await api.post(`/api/sessions/${sessionId}/user-stories/avatar-review`, {
        scope: { type: 'persona', persona: persona.name },
      });
      toast.success(`Asked ${res.data.avatarCount} avatar${res.data.avatarCount === 1 ? '' : 's'} to review ${res.data.storyCount} stories`);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Failed to request avatar review');
    }
  }, [sessionId, persona.name]);

  const renderStoryRow = (story: UserStory, idx: number) => {
    const status = getApprovalStatus(story);
    const isEditing = editingStoryId === story.id;
    const expanded = expandedStories.has(story.id);
    const rowBg = getRowBackground(isEditing, status);
    return (
      <div key={story.id} className={`px-4 py-3 transition-colors ${rowBg}`}>
        {isEditing && editDraft ? (
          <StoryEditForm
            idx={idx}
            draft={editDraft}
            saving={savingEdit}
            onChange={setEditDraft}
            onSave={() => handleSaveEdit(story.id)}
            onCancel={handleCancelEdit}
            onUpdateAc={updateAcLine}
            onRemoveAc={removeAcLine}
            onAddAc={addAcLine}
          />
        ) : (
          <StoryViewRow
            story={story}
            idx={idx}
            status={status}
            expanded={expanded}
            onToggleExpand={() => handleToggleExpand(story.id)}
            onStartEdit={() => handleStartEdit(story)}
            onApprove={() => handleApprovalToggle(story.id, status, 'approved')}
            onReject={() => handleApprovalToggle(story.id, status, 'rejected')}
          />
        )}
      </div>
    );
  };

  const visibleMessages = messages.filter(m => m.personaId === persona.id || m.personaName === 'Facilitator');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 font-display p-4">
      <div className="bg-white rounded-2xl shadow-2xl flex flex-col w-full max-w-7xl max-h-[90vh] overflow-hidden">

        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm flex-shrink-0">
              {persona.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <h2 className="text-base font-bold text-navy leading-tight">{persona.name}</h2>
              <p className="text-xs text-slate-500">Story Review</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleAvatarReviewAll}
              disabled={stories.length === 0}
              className="flex items-center gap-1.5 rounded-xl h-8 px-3 border border-primary/30 text-primary text-xs font-semibold hover:bg-primary/5 transition-colors disabled:opacity-50"
              title="Ask session avatars (not this persona) to review all its stories"
            >
              <Sparkles size={12} /> Ask avatars to review
            </button>
            <button
              onClick={onClose}
              className="flex items-center justify-center w-8 h-8 rounded-xl border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 transition-colors"
            >
              <X size={15} />
            </button>
          </div>
        </div>

        {/* Body: two-column */}
        <div className="flex flex-1 min-h-0 overflow-hidden">

          {/* Left: story list — grows to absorb extra modal width */}
          <div className="flex-1 border-r border-slate-200 flex flex-col overflow-hidden">
            <div className="px-4 py-2 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between flex-shrink-0">
              <p className="text-[11px] font-bold text-primary tracking-widest uppercase">Stories</p>
              <span className="text-xs text-slate-400">
                {stories.filter(s => getApprovalStatus(s) === 'approved').length}/{stories.length} approved
              </span>
            </div>
            {stories.length === 0 ? (
              <div className="flex-1 flex items-center justify-center text-slate-400 text-sm px-4 text-center">No stories for this persona</div>
            ) : (
              <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
                {stories.map((story, idx) => renderStoryRow(story, idx))}
              </div>
            )}
          </div>{/* end left column */}

          {/* Right: chat — fixed width to match original */}
          <div className="w-[44rem] flex-shrink-0 flex flex-col overflow-hidden">

            {/* Chat thread */}
            <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-3 min-h-0">
              {visibleMessages.length === 0 && !analyzing && (
                <div className="flex flex-col items-center justify-center h-full text-center gap-3 py-8">
                  <div className="flex gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-50 border border-blue-200 flex items-center justify-center">
                      <Brain size={18} className="text-blue-500" />
                    </div>
                    <div className="w-10 h-10 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center">
                      <User size={18} className="text-primary" />
                    </div>
                  </div>
                  <p className="text-slate-500 text-sm">UX Specialist + {persona.name} are ready</p>
                  <button
                    onClick={handleAnalyze}
                    className="flex items-center gap-2 rounded-xl h-9 px-5 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm"
                  >
                    <Sparkles size={14} /> Analyse stories
                  </button>
                </div>
              )}

              {visibleMessages.map(msg => (
                <ChatMessage
                  key={msg.id}
                  message={msg}
                  personaName={persona.name}
                  onAction={handleAction}
                  executingAction={executingAction}
                />
              ))}

              {analyzing && (
                <div className="flex items-center gap-3 px-3 py-2">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 rounded-full bg-slate-300 animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 rounded-full bg-slate-300 animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 rounded-full bg-slate-300 animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                  <span className="text-xs text-slate-400">Agents thinking…</span>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Chat input */}
            <div className="border-t border-slate-200 px-4 py-3 bg-white flex-shrink-0">
              <div className="flex items-center gap-2 mb-2">
                <button
                  onClick={handleAnalyze}
                  disabled={analyzing}
                  className="flex items-center gap-1.5 rounded-xl h-8 px-3 border border-primary/30 text-primary text-xs font-semibold hover:bg-primary/5 transition-colors disabled:opacity-50"
                >
                  <RefreshCw size={12} className={analyzing ? 'animate-spin' : ''} />
                  Re-analyse
                </button>
                <button
                  onClick={handleClearChat}
                  className="flex items-center gap-1.5 rounded-xl h-8 px-3 border border-slate-200 text-slate-500 text-xs font-semibold hover:bg-slate-50 transition-colors"
                >
                  <Trash2 size={12} /> Clear chat
                </button>
              </div>
              <div className="flex gap-2">
                <input
                  value={userMessage}
                  onChange={e => setUserMessage(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleUserMessage(); } }}
                  placeholder="Ask a follow-up question…"
                  className="flex-1 border border-slate-200 rounded-xl px-3 py-2 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                />
                <button
                  onClick={handleUserMessage}
                  disabled={!userMessage.trim() || analyzing}
                  className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary hover:bg-primary-dark transition-colors text-white disabled:opacity-50"
                >
                  <Send size={14} />
                </button>
              </div>
            </div>

          </div>{/* end right column */}
        </div>{/* end two-column body */}
      </div>
    </div>
  );
};
